// ============================================================
// EFFECTS THAT DEPEND ON THE SITUATION - applied to one attack.
//
// An effect with a condition (`effectWhen`) is not part of what a character
// simply is, so the resolver leaves it out. It is applied HERE, to the
// attack about to be made, once the situation is known - who is targeted,
// with what kind of weapon, in what state:
//
//   - a bonus to the attack skill becomes a LINE in the attack's modifier
//     list, so the sum, the ledger and the forecast all carry it, and the
//     line says which stance it came from and why it applies;
//   - a bonus to damage is added to the stats the hit reads.
//
// One function, one place it is called (`hooks/useSituationalEffects`), so
// the roll, the forecast and the ledger cannot disagree.
//
// What cannot be told yet (nobody targeted) adds nothing and is returned
// as `waiting`, in words.
//
// Pure. Zero React dependency.
// ============================================================
import { holds, whenSentence } from './effectWhen';
import type { Situation } from './effectWhen';
import { effectSentence, isSheetKind, readEffects } from './effectItems';
import type { StanceEffect } from './effectItems';
import type { AttackerCombatStats, Character } from './types';

/** A line the situation adds to the attack's modifiers: the shape the custom list already holds. */
interface SituationalLine {
  id: string;
  label: string;
  value: number;
  derived: true;
}

export interface Situated {
  /** The stats the hit reads: the same object when nothing was added to them. */
  stats: AttackerCombatStats | null;
  mods: SituationalLine[];
  /** Who grants a re-roll of this attack's damage (a stance's name), or null. */
  rerollDamage: string | null;
  /** Effects whose condition cannot be judged yet, as sentences. */
  waiting: string[];
}

/**
 * The effects of this (effective) sheet that are applied AT THE ATTACK:
 * anything with a condition, and every re-roll of the attack (which the
 * resolver never reads, conditional or not).
 */
export function attackEffects(sheet: Pick<Character, 'permanentEffects'>): StanceEffect[] {
  // The sheet tier is the effective sheet's (`roster/stancedSheet`), conditional or not: never the attack's.
  return readEffects(sheet.permanentEffects).filter(
    (effect) => !isSheetKind(effect.type) && (effect.when || effect.type === 'attack_reroll'),
  );
}

type Kind = Situation['attack'];

/** Is this effect about the kind of attack being made? With no weapon armed, it may be. */
function concerns(effect: StanceEffect, attack: Kind): boolean {
  if (!attack || effect.type === 'attack_reroll') return true;
  if (effect.type === 'skill_bonus') return effect.skill === (attack === 'melee' ? 'ws' : 'bs');
  return effect.type === 'damage_bonus' && (effect.scope === 'all' || effect.scope === attack);
}

/** The stats with `value` more damage on this kind of attack. */
function withDamage(stats: AttackerCombatStats, attack: Kind, value: number): AttackerCombatStats {
  const key = attack === 'melee' ? 'damageBonusMelee' : 'damageBonusRanged';
  return { ...stats, [key]: (stats[key] ?? 0) + value };
}

const from = (effect: StanceEffect) => effect.source ?? 'Always on';

type Apply<K extends StanceEffect['type']> = (
  out: Situated,
  effect: Extract<StanceEffect, { type: K }>,
  i: number,
  attack: Kind,
) => void;

/** How each kind that is applied at the attack lands on what the attack will read. A lookup: a new kind is a row. */
const APPLY: { [K in StanceEffect['type']]?: Apply<K> } = {
  skill_bonus: (out, effect, i) => {
    const label = `${from(effect)}: ${whenSentence(effect.when ?? [])}`;
    out.mods.push({ id: `when-${i}`, label, value: effect.value, derived: true });
  },
  damage_bonus: (out, effect, _i, attack) => {
    if (out.stats) out.stats = withDamage(out.stats, attack, effect.value);
  },
  attack_reroll: (out, effect) => {
    if (effect.of === 'damage') out.rerollDamage ??= from(effect);
    else if (out.stats) out.stats = { ...out.stats, situationalReroll: out.stats.situationalReroll ?? from(effect) };
  },
};

/**
 * Does it hold? With no weapon armed there is no attack to apply anything to: it waits (null).
 * An effect with no condition (a plain re-roll) holds for every attack.
 */
function verdictOf(effect: StanceEffect, situation: Situation): boolean | null {
  if (!situation.attack) return null;
  return effect.when ? holds(effect.when, situation) : true;
}

/**
 * Apply the effects that hold in this situation.
 *
 * @param stats - the attacker's resolved stats, or null (nothing to add damage to)
 * @param effects - `attackEffects(effectiveSheet(actor))`
 */
export function situate(stats: AttackerCombatStats | null, effects: StanceEffect[], situation: Situation): Situated {
  const out: Situated = { stats, mods: [], rerollDamage: null, waiting: [] };
  effects.forEach((effect, i) => {
    if (!concerns(effect, situation.attack)) return;
    const verdict = verdictOf(effect, situation);
    if (verdict === null) out.waiting.push(effectSentence(effect));
    if (verdict) (APPLY[effect.type] as Apply<StanceEffect['type']> | undefined)?.(out, effect, i, situation.attack);
  });
  return out;
}
